"use client";

import React from "react";

import { useTranslations } from "next-intl";

import { priceFormat } from "@/shared.utils/number-format";
import { PrimDialog } from "@/shared-web.ui/prim.dialog";
import { Button } from "@/shared-web.ui/ui.button";
import { DangerouslyHtmlContent } from "@/shared-web.ui/ui.dangerously-html-content";
import { ScrollAreaFlex } from "@/shared-web.ui/ui.scroll-area";
import { Separator } from "@/shared-web.ui/ui.separator";
import { Text } from "@/shared-web.ui/ui.text";
import { type SubscriptionCoffee } from "@/stf.data/schema";

import { SubsCoffeeCard } from "../ui.subs-coffee-card";

type PackageCardProps = {
  emoji: React.ReactNode;
  title: React.ReactNode;
  tasteNotes: React.ReactNode;
  startingPrice: number;
  startingSize: string;
  description: string;
  sampleCoffees: SubscriptionCoffee[];
};

const CoffeePackagesHeaders = (props: PackageCardProps) => {
  const { emoji, title, tasteNotes, startingPrice, startingSize } = props;

  const t = useTranslations("stf_m_subscription.screen_subscription.package_selector");

  return (
    <div className={`
      grid grid-cols-[60px_1fr] gap-x-4 gap-y-0 p-2
    `}
    >
      <div className={`
        row-span-2 flex size-[60px] items-center
        justify-center
      `}
      >
        <span className="text-[28px]">
          {emoji}
        </span>
      </div>
      <div className="flex flex-col justify-center">
        <Text size="2" align="left" color="gray">
          {title}
        </Text>
        <Text size="3" weight="medium" align="left" semanticColor="accent">
          {tasteNotes}
        </Text>
      </div>
      <div>
        <Text size="2" semanticColor="accent" align="left">
          {t("start_at_price", { price: priceFormat(startingPrice), size: startingSize }) }
        </Text>
      </div>
    </div>
  );
};

const LearnMore = (props: PackageCardProps) => {
  const { sampleCoffees } = props;

  const t = useTranslations("stf_m_subscription.screen_subscription.package_selector");

  const [open, setOpen] = React.useState(false);

  return (
    <PrimDialog.Root open={open} onOpenChange={setOpen}>
      <PrimDialog.Trigger asChild>
        <Button
          variant="soft"
          size="3"
          className="w-full"
        >
          {t("see_the_coffees")}
        </Button>
      </PrimDialog.Trigger>
      <PrimDialog.Content
        className={`
          flex size-full min-h-48 flex-col gap-0
          overflow-hidden p-0 rounded-2 py-2
          sm:max-h-[80vh] sm:max-w-md
        `}
      >
        <PrimDialog.Title
          className="hidden"
        >
          Coffee Previews
        </PrimDialog.Title>
        <CoffeePackagesHeaders
          {...props}
        />
        <Separator size="4" className="my-2" />
        <div className="relative grow">
          <ScrollAreaFlex>
            <div className="flex flex-col gap-4 p-4">
              {sampleCoffees.map((coffee) => {
                return (
                  <SubsCoffeeCard
                    key={coffee.coffeeName + coffee.roasters}
                    collapseButtonColor="gray"
                    defaultOpen
                    showWeight={false}
                    className="p-4"
                    {...coffee}
                  />
                );
              })}
            </div>
          </ScrollAreaFlex>
        </div>
        <Separator size="4" className="my-2" />
        <div className="px-4 pb-2">
          <Text size="2" semanticColor="accent">
            {t("coffee_sample_notes")}

          </Text>
        </div>
        <div className="px-2">
          <Button
            className="w-full"
            variant="soft"
            size="3"
            onClick={() => { return setOpen(false); }}
          >
            {t("back_to_selection")}
          </Button>
        </div>
      </PrimDialog.Content>
    </PrimDialog.Root>
  );
};

export const PackageCard = (props: PackageCardProps) => {
  const { description } = props;

  return (
    <div className="flex size-full flex-col p-2">
      <CoffeePackagesHeaders
        {...props}
      />
      <Separator size="4" className="my-2" />
      <div className="flex grow flex-col justify-between p-2">
        <div
          className="prose text-gray-11"
        >
          <DangerouslyHtmlContent
            dangerouslyHtmlContent={description}
          />
        </div>
      </div>
      <Separator size="4" className="my-2" />
      <div className="flex w-full py-1">
        <LearnMore
          {...props}
        />
      </div>
    </div>
  );
};
